import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";

import { useTranslation } from "react-i18next";

import Overlay from "ol/Overlay";

import {
  Circle as CircleStyle,
  Fill,
  Stroke,
  Style,
} from "ol/style";

import { getArea } from "ol/sphere";
import { unByKey } from "ol/Observable";
import Draw from "ol/interaction/Draw";

import "./AreaMeasurer.css";

import { source } from "../../_config/layers/vector";
import { vector } from "../../_config/layers/";

import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";

let drawArea = null;
let sketchArea = null;

let helpTooltipElementArea = null;
let helpTooltipArea = null;

let measureTooltipElementArea = null;
let measureTooltipArea = null;

const AreaMeasurer = forwardRef(
  ({ map, activeTool, handleActiveTool }, ref) => {
    const { t } = useTranslation();

    const htmlElement = useRef(null);

    const [active, setActive] = useState(false);

    const formatArea = (polygon) => {
      const area = getArea(polygon, {
        projection: "EPSG:4326",
      });

      return area > 10000
        ? `${Math.round((area / 1000000) * 100) / 100} ${t(
            "areaMeasurer.km2"
          )}`
        : `${Math.round(area * 100) / 100} ${t(
            "areaMeasurer.m2"
          )}`;
    };

    const pointerMoveHandler = (evt) => {
      if (
        evt.dragging ||
        !helpTooltipElementArea ||
        !helpTooltipArea
      ) {
        return;
      }

      helpTooltipElementArea.innerHTML = sketchArea
        ? t("areaMeasurer.continueMsg")
        : t("areaMeasurer.initMsg");

      helpTooltipArea.setPosition(evt.coordinate);

      helpTooltipElementArea.classList.remove("hidden");
    };

    const createHelpTooltip = () => {
      if (helpTooltipElementArea) {
        helpTooltipElementArea.remove();
      }

      helpTooltipElementArea =
        document.createElement("div");

      helpTooltipElementArea.className =
        "ol-tooltip hidden";

      helpTooltipArea = new Overlay({
        element: helpTooltipElementArea,
        offset: [15, 0],
        positioning: "center-left",
      });

      map.addOverlay(helpTooltipArea);
    };

    const createMeasureTooltip = () => {
      if (measureTooltipElementArea) {
        measureTooltipElementArea.remove();
      }

      measureTooltipElementArea =
        document.createElement("div");

      measureTooltipElementArea.className =
        "ol-tooltip ol-tooltip-measure";

      measureTooltipArea = new Overlay({
        element: measureTooltipElementArea,
        offset: [0, -15],
        positioning: "bottom-center",
        stopEvent: false,
        insertFirst: false,
      });

      map.addOverlay(measureTooltipArea);
    };

    const addInteraction = () => {
      if (!map) {
        return;
      }

      if (drawArea) {
        map.removeInteraction(drawArea);
        drawArea = null;
      }

      drawArea = new Draw({
        source,
        type: "Polygon",
        style: new Style({
          fill: new Fill({
            color: "rgba(255,255,255,0.2)",
          }),
          stroke: new Stroke({
            color: "rgba(0,0,0,0.5)",
            lineDash: [10, 10],
            width: 2,
          }),
          image: new CircleStyle({
            radius: 5,
            stroke: new Stroke({
              color: "rgba(0,0,0,0.7)",
            }),
            fill: new Fill({
              color: "rgba(255,255,255,0.2)",
            }),
          }),
        }),
      });

      map.addInteraction(drawArea);

      createMeasureTooltip();
      createHelpTooltip();

      let listener = null;

      drawArea.on("drawstart", (evt) => {
        sketchArea = evt.feature;

        let tooltipCoord = evt.coordinate;

        listener = sketchArea
          .getGeometry()
          .on("change", (evt) => {
            const geom = evt.target;

            if (
              !measureTooltipElementArea ||
              !measureTooltipArea
            ) {
              return;
            }

            measureTooltipElementArea.innerHTML =
              formatArea(geom);

            tooltipCoord = geom
              .getInteriorPoint()
              .getCoordinates();

            measureTooltipArea.setPosition(
              tooltipCoord
            );
          });
      });

      drawArea.on("drawend", () => {
        if (measureTooltipElementArea) {
          measureTooltipElementArea.className =
            "ol-tooltip ol-tooltip-static";
        }

        if (measureTooltipArea) {
          measureTooltipArea.setOffset([0, -7]);
        }

        sketchArea = null;

        if (listener) {
          unByKey(listener);
          listener = null;
        }

        measureTooltipElementArea = null;

        createMeasureTooltip();
      });
    };

    const handleActivity = (activate) => {
      if (!map) {
        return;
      }

      if (activate) {
        map.on(
          "pointermove",
          pointerMoveHandler
        );

        createHelpTooltip();
        createMeasureTooltip();

        addInteraction();
      } else {
        map.un(
          "pointermove",
          pointerMoveHandler
        );

        if (drawArea) {
          map.removeInteraction(drawArea);
          drawArea = null;
        }

        if (helpTooltipArea) {
          map.removeOverlay(helpTooltipArea);
          helpTooltipArea = null;
        }

        if (helpTooltipElementArea) {
          helpTooltipElementArea.remove();
          helpTooltipElementArea = null;
        }

        if (measureTooltipArea) {
          map.removeOverlay(measureTooltipArea);
          measureTooltipArea = null;
        }

        measureTooltipElementArea = null;
        sketchArea = null;

        source.clear();

        document
          .querySelectorAll(".ol-tooltip-static")
          .forEach((el) => el.remove());
      }
    };

    useImperativeHandle(ref, () => ({
      deactivate: () => {
        setActive(false);

        if (handleActiveTool) {
          handleActiveTool(null);
        }
      },
    }));

    useEffect(() => {
      handleActivity(active);

      return () => {
        handleActivity(false);
      };
    }, [active]);

    useEffect(() => {
      if (
        activeTool &&
        activeTool !== htmlElement
      ) {
        setActive(false);
      }
    }, [activeTool]);

    const handleClick = () => {
      const next = !active;

      vector.getSource().clear();
      source.clear();

      setActive(next);

      if (handleActiveTool) {
        handleActiveTool(
          next ? htmlElement : null
        );
      }
    };

    return (
      <>
        <Button
          title={t("areaMeasurer.buttonTooltip")}
          alt={t("areaMeasurer.buttonTooltip")}
          onClick={handleClick}
          ref={htmlElement}
          className={`AreaMeasurerButton AreaMeasurer ${
            active
              ? "AreaMeasurerActive"
              : "AreaMeasurerDeactive"
          }`}
          severity="info"
          rounded
          icon="pi pi-draw-polygon"
        />

        <Tooltip
          target=".AreaMeasurerButton"
          content={t("areaMeasurer.buttonTooltip")}
          position="bottom"
          className="CustomTooltip"
        />
      </>
    );
  }
);

AreaMeasurer.displayName = "AreaMeasurer";

export default AreaMeasurer;